/*
Account Vuex Module
Jonathan Hill, May 2019.

This module is for managing the user/team 'account' details in the app. It should not be confused with the
business plan 'account' that is attached to teams in TITAN.

Ideally this should be refactored to avoid further confusion.
 */

import Vue from 'vue';
import api from '@/services/api';
const jwt_decode = require('jwt-decode');

function hasTokenExpired(token) {
  if (token) {
    const decodedToken = jwt_decode(token);
    const currentTime = Math.floor(new Date().getTime() / 1000);
    const adjustedTime = currentTime + 7200;
    if (decodedToken.exp && decodedToken.exp > adjustedTime) {
      return false;
    }
  }
  return true;
}

function handleError(error) {
  return {
    error: true,
    message: error.message,
    statusCode: (error.response && error.response.status) || 'unknown'
  };
}
async function checkToken(state, dispatch) {
  const inUseToken = Vue.axios.defaults.headers.common['Authorization'];
  if (!inUseToken) {
    if (state.token && !hasTokenExpired(state.token)) {
      Vue.axios.defaults.headers.common[
        'Authorization'
      ] = `Bearer ${state.token}`;
    } else {
      const { token } = await dispatch('auth/refresh', {}, { root: true });
      if (token) {
        Vue.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    }
  } else {
    // remove 'bearer ' from start of in-use-token
    const testToken = inUseToken.length > 7 ? inUseToken.slice(7) : inUseToken;
    if (hasTokenExpired(testToken)) {
      const { token } = await dispatch('auth/refresh', {}, { root: true });
      if (token) {
        Vue.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
    }
  }
}

export default {
  namespaced: true,
  state: {
    teamId: null,
    invitations: [],
    downloads: [],
    groups: [],
    billingHistory: []
  },
  getters: {
    billingHistory: state => {
      if (state && state.billingHistory) {
        return state.billingHistory || [];
      }
      return [];
    }
  },
  mutations: {
    SET_TEAM(store, data) {
      store.teamId = data._id;
    },
    SET_INVITATIONS(store, data) {
      store.invitations = data;
    },
    SET_DOWNLOADS(store, data) {
      store.downloads = data;
    },
    SET_GROUPS(store, data) {
      store.groups = data;
    },
    RESET_ALL(store) {
      // store.account = null
      store.teamId = null;
      store.keys = null;
      store.invitations = [];
      store.billingHistory = [];
      store.groups = [];
      store.downloads = [];
    },
    SET_BILLING_HISTORY(store, data) {
      store.billingHistory = data;
    }
  },
  actions: {
    async signup({ commit }, data) {
      try {
        // await checkToken(rootState, dispatch);
        const { data: response } = await api.signup(data);
        if (response && response.success) {
          commit('SET_TEAM', response.team);
        }
        return response;
      } catch (error) {
        return handleError(error);
      }
    },
    async changePlan({ dispatch, rootState }, data) {
      // authenticate, then send existing subscription_id and new plan_id to titan
      // which will interface with Paddle and request change.
      // Paddle will then fire our subscription_updated webhook.
      try {
        await checkToken(rootState, dispatch);
        // will have to wait for the subscription_update webhook event
        const { data: response } = await api.changePlan(
          data.subscription_id,
          data.plan_id
        );
        if (response && response.success) {
          // This is just a rough test and may not accomplish anything of use
          // Only of value if webhook has fired
          // Replace this code with new SMEE listener
          dispatch('auth/getAccount', {}, { root: true });
        }
        return response;
      } catch (error) {
        return handleError(error);
      }
    },
    async getHistory({ commit, rootState, dispatch }) {
      try {
        await checkToken(rootState, dispatch);
        // will have to wait for the subscription_update webhook event
        const { data: response } = await api.getHistory();
        commit('SET_BILLING_HISTORY', response);
        return response;
      } catch (error) {
        return handleError(error);
      }
    },
    async updateProfile({ dispatch, rootState }, data) {
      try {
        const userId = data.userId;
        delete data.userId;
        await checkToken(rootState, dispatch);
        const { data: response } = await api.updateProfile(userId, data);
        if (response) {
          if (!response.error) {
            if (userId === rootState.auth.user._id) {
              // console.log('Reloading user');
              dispatch('auth/reloadUser', response, { root: true });
            }
            // console.log('reloading getTeam...');
            dispatch('auth/getTeam', {}, { root: true });
          }
          return response;
        }
      } catch (error) {
        return handleError(error);
      }
    },
    async updateTeam({ dispatch, rootState }, data) {
      try {
        const teamId = data.teamId;
        delete data.teamId;
        await checkToken(rootState, dispatch);
        const { data: response } = await api.updateTeamProfile(teamId, data);
        if (response) {
          if (!response.error) {
            // eslint-disable-next-line
            // console.log('Reloading team');
            dispatch('auth/reloadTeam', response, { root: true });
          }
          return response;
        }
      } catch (error) {
        return handleError(error);
      }
    },
    // eslint-disable-next-line no-unused-vars
    async updatePassword({ commit }, data) {
      try {
        // await checkToken(rootState, dispatch);
        const { data: response } = await api.updatePassword(data);
        return response;
      } catch (error) {
        return handleError(error);
      }
    },
    // eslint-disable-next-line no-unused-vars
    async forgotPassword({ commit }, data) {
      try {
        // await checkToken(rootState, dispatch);
        // console.log('Forgot password request:', data);
        const { data: response } = await api.forgotPassword(data);
        return response;
      } catch (error) {
        return handleError(error);
      }
    },
    // eslint-disable-next-line no-unused-vars
    async resetPassword({ commit }, data) {
      try {
        // await checkToken(rootState, dispatch);
        const { data: response } = await api.resetPassword(data);
        return response;
      } catch (error) {
        return handleError(error);
      }
    },
    // eslint-disable-next-line no-unused-vars
    async verifyEmail({ commit }, data) {
      try {
        // checkToken(rootState);
        const { data: response } = await api.verifyEmail(data);
        return response;
      } catch (error) {
        return handleError(error);
      }
    },
    // eslint-disable-next-line no-unused-vars
    async sendVerificationEmail({ commit }, data) {
      try {
        // await checkToken(rootState, dispatch);
        const { data: response } = await api.sendVerificationEmail(data);
        return response;
      } catch (error) {
        return handleError(error);
      }
    },
    async getDownloads({ commit, rootState, dispatch }) {
      try {
        await checkToken(rootState, dispatch);
        const { data: response } = await api.getDownloads();
        if (response) {
          if (!response.error) {
            commit('SET_DOWNLOADS', response);
          }
          return response;
        }
        return [];
      } catch (error) {
        return handleError(error);
      }
    },
    // eslint-disable-next-line no-unused-vars
    async getKeys({ commit, rootState, dispatch }) {
      try {
        await checkToken(rootState, dispatch);
        return {
          data: { teamId: rootState.team._id, key: rootState.team.apiKey }
        };
      } catch (error) {
        return handleError(error);
      }
    },
    async getGroups({ commit, rootState, dispatch }) {
      try {
        await checkToken(rootState, dispatch);
        const { data: response } = await api.getGroups();
        console.log('GROUPS: ', response);
        if (response) {
          if (!response.error) {
            commit('SET_GROUPS', response);
          }
          return response;
        }
        return [];
      } catch (error) {
        return handleError(error);
      }
    },
    async updateGroup({ dispatch, rootState }, data) {
      try {
        const groupId = data.groupId;
        await checkToken(rootState, dispatch);
        const { data: results } = await api.updateGroup(groupId, data);
        await dispatch('getGroups');
        return results;
      } catch (error) {
        return handleError(error);
      }
    },
    async deleteGroup({ dispatch, rootState }, groupId) {
      try {
        await checkToken(rootState, dispatch);
        const { data: results } = await api.deleteGroup(groupId);
        await dispatch('getGroups');
        return results;
      } catch (error) {
        return handleError(error);
      }
    },
    async getInvitations({ commit, rootState, dispatch }) {
      try {
        await checkToken(rootState, dispatch);
        // console.log('getting invites...');
        const { data: response } = await api.getInvitations();
        if (response) {
          if (!response.error) {
            commit('SET_INVITATIONS', response);
          }
          return response;
        }
        return [];
      } catch (error) {
        return handleError(error);
      }
    },
    async deleteInvitation({ dispatch, rootState }, inviteId) {
      try {
        await checkToken(rootState, dispatch);
        const { data: results } = await api.deleteInvitation(inviteId);
        await dispatch('getInvitations');
        return results;
      } catch (error) {
        return handleError(error);
      }
    },
    async sendInvitation({ dispatch, rootState }, email) {
      try {
        await checkToken(rootState, dispatch);
        // console.log('sending invite');
        const { data: results } = await api.sendInvitation(email);
        // console.log('refreshing invites after sending');
        await dispatch('getInvitations');
        return results;
      } catch (error) {
        return handleError(error);
      }
    },
    async leaveTeam({ dispatch, rootState }, data) {
      try {
        await checkToken(rootState, dispatch);
        const { data: response } = await api.leaveTeam(data);
        if (response && !response.error) {
          dispatch('auth/reloadUsers', response, { root: true });
        }
        return response;
      } catch (error) {
        return handleError(error);
      }
    },
    resetAccount({ commit }) {
      commit('RESET_ALL');
    }
  }
};
